import React, { useContext, useEffect, useMemo } from 'react';
import { Field, Form, Formik } from 'formik';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Select } from 'antd';
import { toast } from 'react-toastify';
import { MainLanguageContext } from '../context/MainLanguageContext';
import useGet from '../customHooks/useGet';
import usePost from '../customHooks/usePost';

const initialValues = {
  target_type: 'default', promotion_id: '', code_title: '', code_type: 'percentage', code_value: '', discount_apply_on: 'rental_price',
  code: '', code_status: '1', expires_at: '', usage_limit: '', maximum_discount_cap: '',
  eligible_customer_email: '', eligible_customer_emails: [], eligible_product_ids: [],
};

const inputClass = 'mt-2 h-12 w-full rounded-xl border border-[#DCE4F2] bg-white px-4 text-sm text-[#263238] outline-none transition placeholder:text-[#A5B0C2] focus:border-primary focus:ring-4 focus:ring-primary/10';

const FormField = ({ name, label, type = 'text', placeholder, disabled = false }) => (
  <label className="block text-sm font-medium text-[#52627A]">
    {label}
    <Field type={type} name={name} placeholder={placeholder} disabled={disabled} className={`${inputClass} disabled:cursor-default disabled:bg-[#F6F8FC]`} />
  </label>
);

const UnifiedPromoCodeForm = ({ readOnly = false }) => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const basePath = '/promo';
  const { mainLanguage } = useContext(MainLanguageContext);
  const [details, getDetails] = useGet();
  const [save, post] = usePost();
  const [products, getProducts] = useGet();
  const [promotions, getPromotions] = useGet();

  useEffect(() => {
    if (isEdit && mainLanguage) getDetails(`promo_codes/edit/${id}/${mainLanguage}`);
  }, [id, isEdit, mainLanguage]);

  useEffect(() => {
    if (mainLanguage) getProducts(`products/allCars/${mainLanguage}/0/1`);
  }, [mainLanguage]);

  useEffect(() => {
    if (mainLanguage) getPromotions(`promotions/list/${mainLanguage}/1000?page=1`);
  }, [mainLanguage]);

  const values = useMemo(() => {
    const data = details.data?.data;
    if (!isEdit || !data) return initialValues;
    return {
      ...initialValues,
      ...data,
      target_type: 'default',
      code_status: String(data.code_status ?? '1'),
      eligible_customer_emails: Array.isArray(data.eligible_customer_emails)
        ? data.eligible_customer_emails
        : Array.isArray(data.eligible_customer_email)
          ? data.eligible_customer_email
          : data.eligible_customer_email ? [data.eligible_customer_email] : [],
      eligible_product_ids: Array.isArray(data.eligible_product_ids) ? data.eligible_product_ids.map(String) : [],
    };
  }, [details.data, isEdit]);

  useEffect(() => {
    if (save.error) {
      const backendMessage = save.error?.response?.data?.message;
      toast.error(backendMessage || 'Unable to save promo code.');
      return;
    }

    if (save.data) {
      const { status, message } = save.data;
      if (status === 'false') toast.error(message);
      else {
        if (message) toast.success(message);
        navigate(basePath);
      }
    }
  }, [save.data, save.error, navigate, basePath]);

  const handleSubmit = (formValues) => {
    const body = new FormData();
    ['code_title', 'code_type', 'code_value', 'discount_apply_on', 'code', 'code_status', 'expires_at', 'usage_limit', 'maximum_discount_cap', 'eligible_customer_email'].forEach((key) => {
      if (formValues[key] !== undefined && formValues[key] !== '') body.append(key, formValues[key]);
    });
    body.append('target_type', 'default');
    body.append('promotion_id', formValues.promotion_id || '');
    (formValues.eligible_customer_emails || []).filter(Boolean).forEach((email) => body.append('eligible_customer_email[]', email));
    (formValues.eligible_product_ids || []).filter(Boolean).forEach((productId) => body.append('eligible_product_ids[]', productId));
    post(isEdit ? `promo_codes/update/${id}/${mainLanguage}` : `promo_codes/create/${mainLanguage}`, body);
  };

  if (isEdit && (details.isLoading || !details.data)) return <div className="p-8">Loading promo code...</div>;

  return (
    <section className="max-w-6xl pb-10 pr-10 max-lg:pr-6">
      <Link to={basePath} className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-[#71809A] transition hover:bg-[#EFF4FD] hover:text-primary">← Back to promo codes</Link>
      <div className="mb-6 overflow-hidden rounded-3xl bg-primary px-7 py-7 text-white shadow-sm max-md:px-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">Promotions · Promo codes</p>
            <h1 className="text-2xl font-MluvkaBold">{readOnly ? 'Promo code details' : isEdit ? 'Edit promo code' : 'Create a promo code'}</h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-white/80">{readOnly ? 'Review the promo code, discount settings, and eligible audience.' : 'Set up a discount customers can use at checkout. Manage its audience and availability below.'}</p>
          </div>
          <span className="rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm">{isEdit ? 'Update details' : 'New discount'}</span>
        </div>
      </div>

      <Formik initialValues={values} enableReinitialize onSubmit={handleSubmit}>
        {({ values: current, setFieldValue }) => (
          <Form className="space-y-5">
            <div className="rounded-3xl border border-[#E6ECF5] bg-white p-6 shadow-sm max-md:p-4">
              <div className="mb-5">
                <h2 className="font-MluvkaBold text-[#263238]">Discount details</h2>
                <p className="mt-1 text-sm text-[#8A98AD]">Give this offer a name and choose how the discount is calculated.</p>
              </div>
              <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
                <FormField name="code_title" label="Promo title" placeholder="e.g. Welcome offer" disabled={readOnly} />
                <FormField name="code" label="Promo code" placeholder="e.g. WELCOME20" disabled={readOnly} />
                <label className="block text-sm font-medium text-[#52627A]">Discount type
                  <Field as="select" name="code_type" disabled={readOnly} className={`${inputClass} disabled:cursor-default disabled:bg-[#F6F8FC]`}>
                    <option value="amount">Fixed amount</option><option value="percentage">Percentage</option>
                  </Field>
                </label>
                <label className="block text-sm font-medium text-[#52627A]">Discount applies to
                  <Field as="select" name="discount_apply_on" disabled={readOnly} className={`${inputClass} disabled:cursor-default disabled:bg-[#F6F8FC]`}>
                    <option value="rental_price">Rental price</option>
                    <option value="original_price">Original price</option>
                  </Field>
                </label>
                <FormField name="code_value" label="Discount value" type="number" placeholder="Enter discount value" disabled={readOnly} />
                <FormField name="maximum_discount_cap" label="Maximum discount cap" type="number" placeholder="Optional maximum amount" disabled={readOnly} />
                <FormField name="expires_at" label="Expiry date" type="date" disabled={readOnly} />
                <FormField name="usage_limit" label="Usage limit" type="number" placeholder="Leave blank for unlimited" disabled={readOnly} />
                {/* <label className="block text-sm font-medium text-[#52627A]">Promotion (optional)
                  <Select
                    size="large"
                    disabled={readOnly}
                    showSearch
                    optionFilterProp="label"
                    placeholder={promotions.isLoading ? 'Loading promotions...' : 'Select a promotion'}
                    className="promo-promotion-select mt-2 w-full [&_.ant-select-selector]:!h-12 [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-[#DCE4F2] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!leading-[46px]"
                    value={current.promotion_id ? String(current.promotion_id) : undefined}
                    onChange={(value) => setFieldValue('promotion_id', value)}
                    options={(promotions.data?.data || [])
                      .filter((promotion) => String(promotion.promotion_status) === '1')
                      .map((promotion) => ({ label: promotion.promotion_title || `Promotion #${promotion.id}`, value: String(promotion.id) }))}
                    filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
                  />
                </label> */}
                <div className="flex items-center justify-between rounded-2xl bg-[#F6F8FC] px-4 py-3">
                  <div><span className="block text-sm font-medium text-[#52627A]">Promo code status</span><span className="mt-1 block text-xs text-[#8A98AD]">Control whether customers can use this code</span></div>
                  <button
                    type="button"
                    disabled={readOnly}
                    role="switch"
                    aria-checked={String(current.code_status) === '1'}
                    onClick={() => setFieldValue('code_status', String(current.code_status) === '1' ? '0' : '1')}
                    className="inline-flex shrink-0 items-center gap-2 rounded-full text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <span className={`relative block h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${String(current.code_status) === '1' ? 'bg-primary' : 'bg-slate-300'}`}>
                      <span className={`absolute left-1 top-1 block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${String(current.code_status) === '1' ? 'translate-x-5' : 'translate-x-0'}`} />
                    </span>
                    <span className={String(current.code_status) === '1' ? 'text-primary' : 'text-[#7D8CA7]'}>{String(current.code_status) === '1' ? 'Active' : 'Inactive'}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-[#E6ECF5] bg-white p-6 shadow-sm max-md:p-4">
              <div className="mb-5"><h2 className="font-MluvkaBold text-[#263238]">Audience & products</h2><p className="mt-1 text-sm text-[#8A98AD]">Optionally limit who can redeem this offer and which products it applies to.</p></div>
              <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1">
                <div>
                  <label className="block text-sm font-medium text-[#52627A]">Eligible customer emails</label>
                  {(current.eligible_customer_emails || []).map((email, index) => <div key={index} className="mt-2 flex gap-2"><input type="email" value={email} disabled={readOnly} placeholder="name@example.com" onChange={(event) => { const next = [...current.eligible_customer_emails]; next[index] = event.target.value; setFieldValue('eligible_customer_emails', next); }} className={`${inputClass} mt-0 flex-1 disabled:cursor-default disabled:bg-[#F6F8FC]`} />{!readOnly && <button type="button" aria-label="Remove email" onClick={() => setFieldValue('eligible_customer_emails', current.eligible_customer_emails.filter((_, i) => i !== index))} className="rounded-xl px-3 text-sm text-[#A41139] hover:bg-[#FFF1F4]">Remove</button>}</div>)}
                  {!readOnly && <button type="button" className="mt-3 rounded-full bg-[#EFF4FD] px-4 py-2 text-sm font-medium text-primary transition hover:bg-[#E3EAF9]" onClick={() => setFieldValue('eligible_customer_emails', [...(current.eligible_customer_emails || []), ''])}>+ Add email</button>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#52627A]">Eligible products</label>
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    disabled={readOnly}
                    optionFilterProp="label"
                    placeholder={products.isLoading ? 'Loading products...' : 'Select eligible products'}
                    className="eligible-products-select mt-2 w-full"
                    value={(current.eligible_product_ids || []).map(String)}
                    onChange={(productIds) => setFieldValue('eligible_product_ids', productIds)}
                    options={(products.data?.data || []).map((product) => ({ label: product.product_title || product.title || `Product #${product.id}`, value: String(product.id) }))}
                    filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 rounded-3xl border border-[#E6ECF5] bg-white p-4 shadow-sm">
              <Link to={basePath} className="rounded-full px-6 py-3 text-sm font-medium text-[#71809A] transition hover:bg-[#F6F8FC]">{readOnly ? 'Back to promo codes' : 'Cancel'}</Link>
              {readOnly ? <Link to={`${basePath}/edit/${id}`} className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-95">Edit promo code</Link> : <button type="submit" disabled={save.isLoading} className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60">{save.isLoading ? 'Saving...' : isEdit ? 'Save changes' : 'Create promo code'}</button>}
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default UnifiedPromoCodeForm;
